<template>
  <div>
    <h2>Navegação</h2>

    <form action="" class="md-layout" @submit.prevent="submit">
      <p v-if="!entries.length" class="md-caption md-layout-item md-size-100">
        Nenhum destino. Adicione ao menos um.
      </p>

      <div
        v-for="(entry, index) in entries"
        :key="index"
        class="md-layout-item md-size-100 entry-card"
      >
        <div class="entry-header">
          <span class="md-subheading">Destino {{ index + 1 }}</span>
          <md-button
            class="md-icon-button md-dense"
            @click="() => removeEntry(index)"
          >
            <md-icon>delete</md-icon>
          </md-button>
        </div>

        <md-field>
          <label :for="`tipo-${index}`">Tipo</label>
          <md-select :id="`tipo-${index}`" v-model="entry.type">
            <md-option :value="NavigationTargetType.Url">
              URL externa
            </md-option>
            <md-option :value="NavigationTargetType.View"> View </md-option>
            <md-option :value="NavigationTargetType.Equipamento">
              Equipamento
            </md-option>
          </md-select>
        </md-field>

        <template v-if="entry.type === NavigationTargetType.Url">
          <md-field>
            <label :for="`url-${index}`">URL</label>
            <md-input
              :id="`url-${index}`"
              v-model="entry.url"
              placeholder="https://..."
            />
          </md-field>
          <md-field>
            <label :for="`label-${index}`">Rótulo</label>
            <md-input :id="`label-${index}`" v-model="entry.label" />
          </md-field>
        </template>

        <md-field v-else-if="entry.type === NavigationTargetType.View">
          <label :for="`view-${index}`">View</label>
          <md-select :id="`view-${index}`" v-model="entry.viewId">
            <md-option
              v-for="view in availableViews"
              :key="view.id"
              :value="view.id"
            >
              {{ view.titulo }}
            </md-option>
          </md-select>
        </md-field>

        <md-field v-else-if="entry.type === NavigationTargetType.Equipamento">
          <label :for="`equip-${index}`">Equipamento</label>
          <md-select :id="`equip-${index}`" v-model="entry.equipamentoId">
            <md-option
              v-for="equipamento in equipamentos"
              :key="equipamento.id"
              :value="equipamento.id"
            >
              {{ equipamento.nome }}
            </md-option>
          </md-select>
        </md-field>
      </div>

      <div class="md-layout-item md-size-100 mt-2">
        <md-button class="md-raised" @click="addEntry">
          <md-icon>add</md-icon>
          Adicionar destino
        </md-button>
      </div>

      <div class="mt-3" style="text-align: right; width: 100%">
        <md-button class="md-raised md-accent" @click="() => $emit('close')">
          Cancelar
        </md-button>
        <md-button type="submit" class="md-raised md-primary">
          {{ isEdit ? 'Editar' : 'Cadastrar' }}
        </md-button>
      </div>
    </form>
  </div>
</template>

<script>
import { EventBus, Topics, Types } from '@/event-bus'
import { ComponenteTipo, NavigationTargetType } from '@/utils/componente'
import http from '@/utils/http'
import { isSafeHttpUrl } from '@/utils/validations/url'

export default {
  name: 'ViewNavigationForm',
  props: {
    localId: {
      type: String,
      required: true,
    },
    viewId: {
      type: String,
      required: true,
    },
    componente: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      NavigationTargetType,
      entries: [],
      views: [],
      equipamentos: [],
    }
  },
  computed: {
    isEdit() {
      return this.componente !== null
    },
    availableViews() {
      // Uma view nao navega para si mesma.
      return this.views.filter(
        (view) => String(view.id) !== String(this.viewId)
      )
    },
  },
  async beforeMount() {
    const [viewsResponse, equipamentosResponse] = await Promise.all([
      http.get(`view/local/${this.localId}`),
      http.get(`local/${this.localId}/equipamentos`),
    ])

    this.views = viewsResponse?.views || []
    this.equipamentos = equipamentosResponse?.equipamentos || []

    if (this.isEdit) {
      const entries = this.componente.value.entries || []
      this.entries = entries.map((entry) => this.fromBackend(entry))
    } else {
      this.addEntry()
    }
  },
  methods: {
    addEntry() {
      this.entries.push({
        type: NavigationTargetType.Url,
        url: '',
        label: '',
        viewId: null,
        equipamentoId: null,
      })
    },
    removeEntry(index) {
      this.entries.splice(index, 1)
    },
    fromBackend(entry) {
      if (entry.view) {
        return {
          type: NavigationTargetType.View,
          url: '',
          label: entry.view.label || '',
          viewId: entry.view.viewId,
          equipamentoId: null,
        }
      }
      if (entry.equipamento) {
        return {
          type: NavigationTargetType.Equipamento,
          url: '',
          label: entry.equipamento.label || '',
          viewId: null,
          equipamentoId: entry.equipamento.equipamentoId,
        }
      }
      // Default para URL (inclusive quando a entrada e do tipo url).
      return {
        type: NavigationTargetType.Url,
        url: (entry.url && entry.url.url) || '',
        label: (entry.url && entry.url.label) || '',
        viewId: null,
        equipamentoId: null,
      }
    },
    isInvalidEntry(entry) {
      if (entry.type === NavigationTargetType.Url) return !entry.url
      if (entry.type === NavigationTargetType.View) return entry.viewId == null
      if (entry.type === NavigationTargetType.Equipamento) {
        return entry.equipamentoId == null
      }
      return true
    },
    buildEntry(entry) {
      if (entry.type === NavigationTargetType.View) {
        const view = this.views.find((v) => v.id === entry.viewId)
        return {
          view: {
            // IDs sao long no backend e trafegam como string (LongConverter),
            // para o JS nao perder precisao em snowflake IDs > 2^53. NAO usar
            // Number(): corromperia os ultimos digitos. Enviar a string como
            // esta; o backend converte string->long ao desserializar.
            localId: this.localId,
            viewId: entry.viewId,
            // Rotulo denormalizado a partir do titulo atual da view.
            label: view ? view.titulo : entry.label || '',
          },
        }
      }
      if (entry.type === NavigationTargetType.Equipamento) {
        const equipamento = this.equipamentos.find(
          (e) => e.id === entry.equipamentoId
        )
        return {
          equipamento: {
            localId: this.localId,
            equipamentoId: entry.equipamentoId,
            label: equipamento ? equipamento.nome : entry.label || '',
          },
        }
      }
      return {
        url: {
          url: entry.url,
          label: entry.label || entry.url,
        },
      }
    },
    async submit() {
      if (!this.entries.length) {
        EventBus.$emit(Topics.NOTIFICATIONS, {
          message: 'Adicione ao menos um destino',
          type: Types.WARNING,
        })
        return
      }

      if (this.entries.some(this.isInvalidEntry)) {
        EventBus.$emit(Topics.NOTIFICATIONS, {
          message: 'Preencha todos os destinos',
          type: Types.WARNING,
        })
        return
      }

      // Rejeita esquemas perigosos (javascript:/data:) na origem, para nem
      // armazenar uma URL de navegacao insegura.
      const urlInvalida = this.entries.some(
        (e) => e.type === NavigationTargetType.Url && !isSafeHttpUrl(e.url)
      )
      if (urlInvalida) {
        EventBus.$emit(Topics.NOTIFICATIONS, {
          message: 'URLs devem começar com http:// ou https://',
          type: Types.WARNING,
        })
        return
      }

      const value = { entries: this.entries.map((e) => this.buildEntry(e)) }

      let response
      if (this.isEdit) {
        response = await http.put(`/view/componente`, {
          id: this.componente.id,
          z: this.componente.z,
          value,
        })
      } else {
        response = await http.post(`/view/componente`, {
          tipo: ComponenteTipo.NavigationView,
          viewId: this.viewId,
          value,
        })
      }

      if (response) {
        this.$emit('close')
        this.$emit('updated')
      }
    },
  },
}
</script>

<style scoped>
.entry-card {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 8px 12px;
  margin-top: 12px;
}

.entry-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
